import {
Component,
Injector,
Optional
} from '@angular/core';
import {CommonModule} from "@angular/common";

import { DISPLAY_TYPE, IWidgetConfig, provideAsWidgetRef, Parent, register, styler } from "@wm/components-base";
import {FormsModule} from "@angular/forms";
import {getOrderedDataset} from "../../formWidgetUtils";
import {isUndefined, slice} from "lodash-es";
import {DatasetAwareComponent} from "../dataset-aware.component";
import {setCSS} from "@wm/core";

const DEFAULT_CLS = 'app-ratings';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-rating',
  hostClass: DEFAULT_CLS,
  displayType: DISPLAY_TYPE.INLINE_BLOCK
};
@Component({
  selector: '[wmRating]',
  template: `<div [(ngModel)]="modelByKey" (focus)="onFocus($event)" ngDefaultControl class="ratings-wrapper" aria-multiselectable="true">
    <div [class.disabled]="readonly" class="rating-style">
      <label *ngFor="let rate of ratingItems;"
             [ngClass]="{active : rate.index <= selectedRatingValue}"
             [ngStyle]="{'font-size' :iconsize, color: rate.index <= selectedRatingValue && iconcolor}"
             [title]="rate.label || rate.index"
             [for]="_id + '-' + rate.index">
        <i class="app-icon {{rate.index <= selectedRatingValue ? activeiconclass : inactiveiconclass}}" aria-hidden="true"></i>
        <input #ratingInput type="radio" role="radio" [attr.aria-label]="(rate.label || rate.index) + ' out of ' + ratingItems.length + ' ratings'" [attr.aria-checked]="rate.label === datavalue ? 'true' : 'false'"
               [id]="_id + '-' + rate.index" name="ratings-id" [value]="rate.key || rate.index"/>
      </label>
    </div>
    <label *ngIf="showcaptions" class="caption" [textContent]="caption"></label>
  </div>
  `,
  providers: [
    provideAsWidgetRef(RatingComponent)
  ],
  exportAs: 'wmRating',
  standalone: true,
  imports: [ CommonModule,FormsModule ],
  styles: []
})
export class RatingComponent extends DatasetAwareComponent {
  static initializeProps = register('wm-rating');
  public caption: string;
  public showcaptions: boolean;
  public maxvalue: any;
  public ratingsWidth: any;
  public ratingItems: any;
  public _id: any;
  public iconsize: string;
  public iconcolor: string;
  public onFocus: any;
  public activeiconclass: string = '';
  public inactiveiconclass: string = '';

  constructor(inj: Injector,
              @Optional() viewParent: Parent) {
    super(inj, WIDGET_CONFIG, viewParent);
    styler(this.nativeElement, this);
    this.prepareRatingDataset();
  }

  private _selectedRatingValue: any;

  get selectedRatingValue() {
    return this._selectedRatingValue;
  }

  set selectedRatingValue(val) {
    this._selectedRatingValue = val;
    this.calculateRatingsWidth();
  }

  calculateRatingsWidth(dataVal?: any): any {
    const selectedRating = parseFloat(this.selectedRatingValue),
      starWidth = 0.925,
      maxValue = parseInt(this.maxvalue || this.datasetItems.length, 10) || DEFAULT_RATING;

    dataVal = dataVal || this.datavalue;
    if (dataVal === undefined || dataVal === '' || dataVal === null) {
      this.caption = '';
      return 0;
    }
    if (selectedRating <= maxValue && selectedRating >= 0) {
      return selectedRating * starWidth + 'em';
    }
    if (selectedRating > maxValue) {
      return maxValue * starWidth + 'em';
    }
  }

  protected override onPropertyChange(key: string, nv: any, ov?: any) {
    if (key === 'datavalue') {
      this._selectedRatingValue = nv;
      if (this.datasetItems){
        this.calculateRatingsWidth();
      }
    }
    if (key === 'readonly') {
      if (nv) {
        this.ratingsWidth = this.calculateRatingsWidth();
      }
    } else if (key === 'maxvalue') {
      /** Storing the datavalue in temp variable and assiging back to datavalue
       * after default datalist is prepared to trigger Dataset Aware class select cycle
       * as the datavalue property change is triggering first rather than maxvalue
       * in the rating widget is used inside a datatable
       */
      let tempDataValue = this.datavalue;
      this.prepareRatingDataset();
      // reset all the items.
      this.resetDatasetItems();
      if (!isUndefined(tempDataValue)) {
        this.datavalue = tempDataValue;
      }
    } else {
      super.onPropertyChange(key, nv, ov);
    }
  }

  private prepareRatingDataset() {
    if (!this.datasetItems.length) {
      this.ratingItems = [];
      this.caption = '';
    }

    let ratingItems = [];
    let data: any = this.datasetItems;
    const maxvalue = parseInt(this.maxvalue || this.datasetItems.length, 10);
    const maxValue = (maxvalue > MAX_RATING ? MAX_RATING : maxvalue) || DEFAULT_RATING;

    /**
     * 1. If datasetItems.length is more than maxValue (i.e. 10 ratings) then just extract maxValue of items from datasetItems.
     * 2. If datasetItems are not available then prepare ratings value depending on maxvalue. eg: 1,2,3 .. upto maxvalue
     * 3. If maxvalue / i value is more than datasetItems length, prepare default rating items for i values more than datasetItems.length
     */
    if (data.length && data.length > maxValue) {
      data = slice(data, 0, maxValue);
    }

    for (let i = maxValue; i > 0; i--) {
      if (!data.length) {
        ratingItems.push({key: i, value: i, index: i, label: i});
      } else {
        if (i > data.length) {
          ratingItems.push({key: i, value: i, index: i, label: i});
        } else {
          data = getOrderedDataset(data, 'index:desc');
          ratingItems = ratingItems.concat(data);
          break;
        }
      }
    }

    this.ratingItems = ratingItems;
    if (!data.length) { // constructs default datasetItems when there is no dataset binding.
      this.datasetItems = ratingItems;
    }
  }

}
const MAX_RATING = 10;
const DEFAULT_RATING = 5;

